import React, { useState } from 'react'
import { Outlet, Link, useLocation } from 'react-router-dom'
import {
  LayoutDashboard,
  Users,
  Settings,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Store,
  Calendar,
  LogOut,
  Image as ImageIcon,
  MessageSquare,
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import ProfileAvatar from '../components/ProfileAvatar'
import './DashboardLayout.css'

const DashboardLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [openGroups, setOpenGroups] = useState({
    Partners: true,
    Plans: true,
  })
  const location = useLocation()
  const { logout, user } = useAuth()

  const menuItems = [
    { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/banners', icon: ImageIcon, label: 'Banners' },
    { path: '/users', icon: Users, label: 'Users' },
    { path: '/feedback', icon: MessageSquare, label: 'Feedback' },
    {
      icon: Store,
      label: 'Partners',
      children: [
        { path: '/vendors', label: 'Partners' },
        { path: '/vendor-categories', label: 'Partners Categories' },
      ],
    },
    {
      icon: Calendar,
      label: 'Plans',
      children: [
        { path: '/plans', label: 'Plans' },
        { path: '/plan-quiz', label: 'Questionnaire' },
        { path: '/categories', label: 'Plans Categories' },
      ],
    },
  ]

  const bottomMenuItems = [
    { path: '/settings', icon: Settings, label: 'Settings' },
  ]

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/'
    }
    return location.pathname.startsWith(path)
  }

  return (
    <div className="dashboard-layout">
      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        <div className="sidebar-header">
          <div className="logo-container">
            <img
              src={sidebarOpen ? "/big-Logo.png" : "/Logo.png"}
              alt="IN Logo"
              className="sidebar-logo"
            />
          </div>
          <button
            className="sidebar-toggle-floating"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            title={sidebarOpen ? "Collapse sidebar" : "Expand sidebar"}
          >
            {sidebarOpen ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
          </button>
        </div>

        <nav className="sidebar-nav">
          <div className="nav-main">
            {menuItems.map((item) => {
              const Icon = item.icon

              if (item.children) {
                const groupActive = item.children.some((child) => isActive(child.path))
                const isGroupOpen = openGroups[item.label] ?? groupActive

                const toggleGroup = () => {
                  setOpenGroups((prev) => ({
                    ...prev,
                    [item.label]: !(prev[item.label] ?? groupActive),
                  }))
                }

                return (
                  <div key={item.label} className={`nav-group ${groupActive ? 'active' : ''}`}>
                    <button
                      type="button"
                      className="nav-item nav-group-title"
                      onClick={toggleGroup}
                      aria-expanded={isGroupOpen}
                    >
                      <Icon size={20} />
                      {sidebarOpen && <span>{item.label}</span>}
                      {sidebarOpen && (
                        <ChevronDown
                          size={16}
                          className={`nav-group-chevron ${isGroupOpen ? 'open' : ''}`}
                        />
                      )}
                    </button>

                    {sidebarOpen && isGroupOpen && (
                      <div className="nav-submenu">
                        {item.children.map((child) => (
                          <Link
                            key={child.path}
                            to={child.path}
                            className={`nav-sub-item ${isActive(child.path) ? 'active' : ''}`}
                          >
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                )
              }

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`nav-item ${isActive(item.path) ? 'active' : ''}`}
                >
                  <Icon size={20} />
                  {sidebarOpen && <span>{item.label}</span>}
                </Link>
              )
            })}
          </div>

          <div className="nav-bottom">
            {bottomMenuItems.map((item) => {
              const Icon = item.icon
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`nav-item ${isActive(item.path) ? 'active' : ''}`}
                >
                  <Icon size={20} />
                  {sidebarOpen && <span>{item.label}</span>}
                </Link>
              )
            })}
            <div
              className="nav-item logout-item"
              onClick={logout}
              title="Logout"
              style={{ cursor: 'pointer', color: 'var(--danger)' }}
            >
              <LogOut size={20} />
              {sidebarOpen && <span>Logout</span>}
            </div>
          </div>
        </nav>
      </aside>

      {/* Main Content */}
      <div className={`main-content ${sidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
        {/* Header */}
        <header className="header">
          <div className="header-left">
            {/* Toggle moved to sidebar */}
          </div>

          <div className="header-right">
            <div className="user-menu" title={user?.name || 'Admin'}>
              <ProfileAvatar
                src={user?.profilePhotoUrl ?? user?.profile_photo_url}
                alt="User"
                className="user-avatar"
              />
              <div className="user-info">
                <span className="greeting-text">Hello</span>
                <span className="user-name">{user?.name || 'Admin'}</span>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="content">
          <Outlet />
        </main>
      </div>
    </div>
  )
}

export default DashboardLayout
